import { useState } from 'react';
import { Shield, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { toast } from 'sonner';
import { parseApiError } from '../utils/parseApiError';

// Configuración de URL robusta
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, ''); 

/**
 * Componente reutilizable para verificar identidad del cliente
 * Versión Corregida: Manejo de respuesta única y logs de conexión
 */
export default function VerificacionCliente({ onVerificado, titulo, descripcion }) {
  const [cedula, setCedula] = useState('');
  const [email, setEmail] = useState('');
  const [verificando, setVerificando] = useState(false);

  const formatCedula = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}`;
  };

  const handleCedulaChange = (e) => {
    const formatted = formatCedula(e.target.value);
    setCedula(formatted);
  };

  const handleVerificar = async (e) => {
    e.preventDefault();

    if (!cedula.trim() || !email.trim()) {
      toast.error('Por favor ingresa tu cédula y email');
      return;
    }

    setVerificando(true);
    const targetUrl = `${BACKEND_URL}/api/buscar-expediente`;
    console.log("🔌 Conectando a:", targetUrl);

    try {
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', targetUrl, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.timeout = 15000;
        
        xhr.onload = function() {
          try {
            const parsed = JSON.parse(xhr.responseText);
            resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data: parsed });
          } catch (e) {
            reject(new Error('Respuesta inválida del servidor'));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Error de conexión. Verifica tu internet e intenta nuevamente.'));
        };
        
        xhr.ontimeout = function() {
          reject(new Error('La conexión tardó demasiado. Intenta nuevamente.'));
        };
        
        xhr.send(JSON.stringify({
          cedula: cedula.replace(/[^0-9]/g, '').trim(),
          email: email.trim().toLowerCase()
        }));
      });

      if (data.ok) {
        if (data?.data?.expediente) {
          toast.success(`✅ Expediente encontrado: ${data.data.expediente.numero_expediente || ''}`);
          onVerificado(data.data.expediente);
        } else {
          toast.error('No se encontró un expediente con estos datos');
        }
      } else {
        const errorMsg = parseApiError(data?.data?.detail, 'No se encontró un expediente con estos datos');
        toast.error(errorMsg);
      }

    } catch (error) {
      console.error('Error de conexión:', error);
      toast.error(error.message || 'Error de conexión');
    } finally {
      setVerificando(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center py-12 px-6">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/kompa-logo.png" alt="Kompa" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">{titulo || "Verificación"}</h1>
          <p className="text-gray-400">{descripcion || "Ingresa tus datos para continuar"}</p>
        </div>

        <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-500" />
              Verificación de Identidad
            </CardTitle>
            <CardDescription className="text-gray-400">
              Ingresa tus datos para acceder a tu expediente
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerificar} className="space-y-4">
              {/* Cédula */}
              <div>
                <Label className="text-gray-300">Cédula *</Label>
                <Input
                  value={cedula}
                  onChange={handleCedulaChange}
                  placeholder="12.345.678"
                  maxLength={11}
                  inputMode="numeric"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  data-testid="verif-cedula-input"
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ingresa tu cédula sin la letra V o E
                </p>
              </div>

              {/* Email */}
              <div>
                <Label className="text-gray-300">Email *</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  data-testid="verif-email-input"
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Debe coincidir con el email registrado en tu expediente
                </p>
              </div>

              {/* Nota de seguridad */}
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-300 text-sm">
                  🔒 <strong>Verificación segura:</strong> Usamos tu cédula y email para encontrar tu expediente de forma segura.
                </p>
              </div>

              {/* Botón */}
              <Button
                type="submit"
                disabled={verificando}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
              >
                {verificando ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Verificar y Continuar
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          ¿Problemas para acceder? Escríbenos a{' '}
          <a href="mailto:desactivate@kompa.global" className="text-yellow-500 hover:text-yellow-400">
            desactivate@kompa.global
          </a>
        </p>
      </div>
    </div>
  );
}
